/* jshint browser:true */

/**
 * Utilities to assist with color manipulation.
 */
export default class ColorUtils {
  /**
   * An RGB color value represented as an object.
   * @typedef {Object} RgbObject
   * @property {number} r The red value (0-255).
   * @property {number} g The green value (0-255).
   * @property {number} b The blue value (0-255).
   */

  /**
   * Returns the contrast of two colors.
   * @param {RgbObject} color1 The first color.
   * @param {RgbObject} color2 The second color.
   */
  static getContrast(color1, color2) {
    const luminance1 = this.getLuminance(color1);
    const luminance2 = this.getLuminance(color2);
    const lighterLuminance = Math.max(luminance1, luminance2);
    const darkerLuminance = Math.min(luminance1, luminance2);
    const contrast = (lighterLuminance + .05) / (darkerLuminance + .05);

    return contrast;
  }

  /**
   * Removes the alpha value from an RGBA string.
   * @param {string} rgbaString An RGBA color string (e.g.
   *   'rgb(10, 10, 10, 0.5)').
   * @return {string} Returns an RGB string.
   */
  static removeOpacityFromRgba(rgbaString) {
    const rgbObject = this.getObjectFromRgbString(rgbaString);
    const rgbString = this.getRgbStringFromObject(rgbObject);

    return rgbString;
  }

  /**
   * Returns the contrast of text and a background when a text protection scrim
   * is used between the two.
   * @param {RgbObject} baseColor The base layer color.
   * @param {RgbObject} textColor The text color.
   * @param {RgbObject} overlayColor The scrim color.
   * @param {number} overlayOpacity The scrim opacity.
   */
  static getTextProtectionScrimContrast(
    baseColor,
    textColor,
    overlayColor,
    overlayOpacity
  ) {
    const flattenedColor = this
      .getFlattenedColor(baseColor, overlayColor, overlayOpacity);
    const contrast = this.getContrast(textColor, flattenedColor);

    return contrast;
  }

  /**
   * Returns the luminance of a color.
   * @param {RgbObject} color The color to use.
   */
  static getLuminance(color) {
    const { r, g, b } = color;

    return .2126 * this.getLinearRgb(r) +
      .7152 * this.getLinearRgb(g) +
      .0722 * this.getLinearRgb(b);
  }

  /**
   * Returns a linear RGB color value from an 8-bit RGB color value.
   * @param {number} colorValue An individual 8-bit color value (0-255).
   */
  static getLinearRgb(colorValue) {
    // Convert to sRGB color value, then to linear RGB
    const sRgb = colorValue / 255;
    const linearRgb = (sRgb < .03928) ?
      sRgb / 12.92 :
      Math.pow((sRgb + .055) / 1.055, 2.4);

    return linearRgb;
  }

  /**
   * Returns an object from an RGB color string.
   * @param {string} rgbString An RGB color string (e.g. 'rgb(10, 10, 10)').
   */
  static getObjectFromRgbString(rgbString) {
    const rgbArray = rgbString.split(',').map(x => x.replace(/\D/g, ''));

    return {
      r: rgbArray[0],
      g: rgbArray[1],
      b: rgbArray[2]
    };
  }

  /**
   * Returns an RGB color string from an RGB object.
   * @param {RgbObject} rgbObject The RGB object to use.
   */
  static getRgbStringFromObject(rgbObject) {
    const { r, g, b } = rgbObject;

    return `rgb(${r}, ${g}, ${b})`;
  }

  /**
   * Returns the color of two layers when one overlays the other and the top
   * layer is not fully opaque.
   * @param {RgbObject} baseColor The base layer color.
   * @param {RgbObject} overlayColor The color of the top layer.
   * @param {number} overlayOpacity The opacity of the top layer.
   */
  static getFlattenedColor(baseColor, overlayColor, overlayOpacity) {
    const flattenedColor = {
      r: baseColor.r + (overlayColor.r - baseColor.r) * overlayOpacity,
      g: baseColor.g + (overlayColor.g - baseColor.g) * overlayOpacity,
      b: baseColor.b + (overlayColor.b - baseColor.b) * overlayOpacity
    };

    return flattenedColor;
  }
}
